import { Dispatch, ReactNode, SetStateAction, useEffect, useRef, useState } from 'react';

import { flexRender, Row, Table } from '@tanstack/react-table';

import { Icon } from '../icon';
import { SortIcon } from './sort-icon';
import { TablePagination } from './table-pagination';
import { cn } from '../../lib/utils';
import { Button } from '../shadcn/button';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from '../shadcn/empty';
import { Popover, PopoverTrigger } from '../shadcn/popover';
import { Skeleton } from '../shadcn/skeleton';
import { TableBody, TableCell, Table as TableComponent, TableHead, TableHeader, TableRow } from '../shadcn/table';
import { SearchInput } from '@/new/components/search-input';

export function ScrollableTable<T>({
  table,
  globalFilter,
  setGlobalFilter,
  filterPopover,
  filterCount,
  clearAllFilters,
  plural,
  rowOnClick,
  additionalButtons,
  className,
  searchDataTour,
  tableDataTour,
  loading,
}: {
  table: Table<T>;
  globalFilter: any;
  setGlobalFilter: Dispatch<SetStateAction<any>>;
  filterPopover?: ReactNode;
  filterCount?: number;
  clearAllFilters?: () => void;
  plural: string;
  rowOnClick?: (row: Row<T>) => void;
  additionalButtons?: ReactNode;
  className?: string;
  searchDataTour?: string;
  tableDataTour?: string;
  loading?: boolean;
}) {
  // For applying the fade effect
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Check if scrolled to bottom when data changes and on mount
  useEffect(() => {
    const target = scrollContainerRef.current;
    if (target) {
      setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
    }
  }, [table.getRowCount()]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    setIsScrolledToBottom(target.scrollHeight - target.scrollTop <= target.clientHeight + 1);
  };

  const hasRows = table.getRowModel().rows.length > 0;

  return (
    <section className={cn('-mt-1 flex grow flex-col overflow-hidden px-8 py-1', className)}>
      <div className="my-1 flex w-full items-center gap-2" data-tour={searchDataTour}>
        <SearchInput
          placeholder={`Search ${plural}...`}
          className={cn(filterPopover || clearAllFilters ? 'grow' : 'w-full max-w-md')}
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />

        {filterPopover && (
          <Popover>
            <PopoverTrigger asChild>
              <Button className="relative">
                <Icon className="lucide--funnel-plus" />
                <span>Add Filters</span>
                {filterCount !== undefined && filterCount > 0 && (
                  <span className="absolute -top-2 -right-2 flex size-4.5 items-center justify-center rounded-full border border-teal-400 bg-teal-50 pt-[1px] text-xs font-medium text-teal-600">
                    {filterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>

            {filterPopover}
          </Popover>
        )}

        {clearAllFilters && (
          <Button onClick={clearAllFilters}>
            <Icon className="lucide--funnel-x" />
            <span>Clear Filters</span>
          </Button>
        )}

        {additionalButtons && additionalButtons}
      </div>

      <div
        className="mt-2 grow overflow-auto mask-b-from-80%"
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{ maskSize: isScrolledToBottom ? '100% 120%' : '100% 100%' }}
        data-tour={tableDataTour}
      >
        <TableComponent>
          <TableHeader className="sticky top-0 z-10 bg-white">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    <div
                      className="flex cursor-pointer items-center gap-2 select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon column={header.column} />
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {loading
              ? Array.from({ length: 8 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    {table.getHeaderGroups()[0]?.headers.map((header) => (
                      <TableCell key={header.id}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : table.getRowModel().rows.map((row) => {
                  const rowClassName = (table.options.meta as any)?.getRowClassName?.(row.original);
                  return (
                    <TableRow
                      key={row.id}
                      className={cn(rowOnClick ? 'cursor-pointer' : '', rowClassName)}
                      onClick={rowOnClick ? () => rowOnClick(row) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                      ))}
                    </TableRow>
                  );
                })}
          </TableBody>
        </TableComponent>
        {!loading && !hasRows && (
          <Empty className="my-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--inbox" />
              </EmptyMedia>
              <EmptyTitle>No {plural} found</EmptyTitle>
              <EmptyDescription>
                {globalFilter || clearAllFilters
                  ? `No ${plural} match your current filters. Try adjusting your search or filters.`
                  : `There are no ${plural} to display.`}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>

      <TablePagination className="mb-4" table={table} plural={plural} />
    </section>
  );
}
